import { useId } from 'react'
import type { Ref } from 'react'
import { Card } from '../../components/ui/Card'
import type { components } from '../../lib/api/schema.gen'
import { ShippingAccountPicker } from '../checkout/ShippingAccountPicker'
import type { ShippingAccountView } from '../checkout/checkout-domain'
import { SHIP_METHOD_OPTIONS } from './review-domain'

type RfqShipMethod = components['schemas']['RfqShipMethod']
type AddressView = components['schemas']['AddressView']

/** The sentinel `<option>` value the ship-to `<select>` uses for "＋ Add new address…"
 * (mirrors the design's cart ship-to view-model, `Fictiv Platform.dc.html` L7098
 * `{value:'__new', …}`). Selecting it opens the AddressFormModal instead of choosing an id. */
export const ADD_NEW_ADDRESS_OPTION = '__new'

/** The ship-to `<select>` option label — the design-literal "label · city, state"
 * (`Fictiv Platform.dc.html` L7098: `(a.label||a.name)+' · '+[a.city,a.state].join(', ')`). */
export function shipToAddressOptionLabel(address: AddressView): string {
  const locale = [address.city, address.region].filter((part) => part?.trim()).join(', ')
  return locale ? `${address.label} · ${locale}` : address.label
}

/** UID-359 item 13 (ADR-0118) — the design's "resolved address line" beneath
 * the Ship-to select (`Fictiv Platform.dc.html:4343`,
 * `shipToAddrView.line`), built exactly as the view-model builds it at
 * `dc.html:9718`: `[shipAddr.city, shipAddr.state, shipAddr.zip].filter(Boolean).join(', ')`
 * — city/region/postal only, deliberately NOT the fuller street-address line
 * `checkout-domain.ts`'s `formatShipToAddressLine` renders for checkout's own
 * address cards (that helper also folds in `line1`/`line2`, which this design
 * value never shows). Returns `''` (never rendered, `sc-if`-equivalent) when
 * every part is missing, matching the design's own `''` fallback. */
export function resolvedShipToAddressLine(address: AddressView): string {
  return [address.city, address.region, address.postalCode]
    .filter((part) => part?.trim())
    .join(', ')
}

export interface ShipToAddressFieldProps {
  readonly addresses: readonly AddressView[]
  readonly shipToAddressId: string
  readonly onShipToAddressIdChange: (value: string) => void
  readonly onAddNewAddress: () => void
}

/**
 * UID-359 item 13 (ADR-0118 D2, target row 5) — the design's "Ship to" block
 * (`dc.html:4340-4344`): a saved-address `<select>` plus a resolved
 * "city, region postalCode" line once an address is chosen. Split out of the
 * combined {@link ShippingCard} (below) so `RequestSummaryRail` can mount it
 * BEFORE the Part-production row, per the design's row order — the shipping-
 * METHOD select stays down by the Shipping row instead (see
 * {@link ShippingMethodFields}); in the design these two controls are not
 * adjacent (`dc.html:4341` vs `:4348`).
 *
 * **a11y / test-compatibility (ADR-0118, human-reviewed):** the design draws
 * a visible "Ship to" caption above this select (`dc.html:4341`), but per the
 * ticket's explicit instruction the visible field label is DROPPED here (same
 * treatment as the shipping-method select below) and the select instead
 * carries `aria-label="Ship-to address"` — the exact string this control has
 * always exposed, so `getByLabelText('Ship-to address')` keeps resolving
 * (`ShippingCard.test.tsx`, and ~10 sites in `RfqReviewPage.test.tsx`) without
 * either test file being touched. Recorded here as a deliberate, reported
 * deviation from the design's literal visible caption, not an oversight. */
export function ShipToAddressField({
  addresses,
  shipToAddressId,
  onShipToAddressIdChange,
  onAddNewAddress,
}: ShipToAddressFieldProps) {
  const shipToSelectId = useId()
  const selectedAddress = addresses.find((address) => address.id === shipToAddressId)
  const resolvedLine = selectedAddress ? resolvedShipToAddressLine(selectedAddress) : ''

  return (
    <div className="form-field rfq-summary-shipto">
      <select
        id={shipToSelectId}
        aria-label="Ship-to address"
        value={shipToAddressId}
        onChange={(event) => {
          const value = event.target.value
          if (value === ADD_NEW_ADDRESS_OPTION) {
            onAddNewAddress()
            return
          }
          onShipToAddressIdChange(value)
        }}
        className="form-field-input"
      >
        <option value="">Select a ship-to address</option>
        {addresses.map((address) => (
          <option key={address.id} value={address.id}>
            {shipToAddressOptionLabel(address)}
          </option>
        ))}
        <option value={ADD_NEW_ADDRESS_OPTION}>＋ Add new address…</option>
      </select>
      {/* `dc.html:4343`'s `sc-if` — only rendered once an address resolves to
       * a non-empty line, never a placeholder dash. */}
      {resolvedLine && <p className="rfq-summary-shipto-resolved">{resolvedLine}</p>}
    </div>
  )
}

export interface ShippingMethodFieldsProps {
  readonly shipMethod: RfqShipMethod | 'none'
  readonly onShipMethodChange: (value: RfqShipMethod | 'none') => void
  // ── own-account (UID-201, ADR-0076): a picker over the saved shipping-account book ──
  readonly shippingAccounts: readonly ShippingAccountView[]
  readonly selectedShippingAccountId: string | null
  readonly onSelectShippingAccount: (id: string) => void
  readonly onAddShippingAccount: () => void
  readonly shipAccountError?: string | undefined
  /** Focus target for the own-account validation error (the error text itself, tabbable -1). */
  readonly shipAccountErrorRef?: Ref<HTMLParagraphElement> | undefined
}

/**
 * UID-359 item 13 (ADR-0118 D2, target row 7) — the design's shipping-method
 * `<select>` plus the conditional own-account picker (`dc.html:4348-4351`),
 * which the design mounts immediately BELOW the Shipping $ row, not grouped
 * with "Ship to" (see {@link ShipToAddressField}'s doc comment). Split out of
 * {@link ShippingCard} so `RequestSummaryRail` can place it there.
 *
 *  - **Shipping method** — a `<select>` of `SHIP_METHOD_OPTIONS` with
 *    lead-time labels and a placeholder "Select a shipping method" that maps
 *    to omitting `shipMethod` from the wire (ADR-0076 Decision 3; the enum is
 *    unchanged).
 *  - **Own account** — when `own_account` is chosen, the shared
 *    {@link ShippingAccountPicker} over the saved shipping-account book
 *    (UID-206), sending the chosen id as `shippingAccountId` (resolve-on-write
 *    server-side; the full account number never touches the client).
 *
 * **a11y / test-compatibility (ADR-0118, human-reviewed):** the design has no
 * visible label over this select at all (`dc.html:4348` — unlike Ship-to, it
 * is not even preceded by a caption div), so dropping the build's former
 * visible "Shipping method" `<label>` is design-literal. The accessible name
 * is preserved via `aria-label="Shipping method"` — the same string the
 * dropped `<label>` used to supply — so `getByLabelText('Shipping method')`
 * keeps resolving without touching `ShippingCard.test.tsx` or the ~10 sites
 * in `RfqReviewPage.test.tsx` that depend on it. */
export function ShippingMethodFields({
  shipMethod,
  onShipMethodChange,
  shippingAccounts,
  selectedShippingAccountId,
  onSelectShippingAccount,
  onAddShippingAccount,
  shipAccountError,
  shipAccountErrorRef,
}: ShippingMethodFieldsProps) {
  const shipMethodSelectId = useId()
  const shipAccountErrorId = useId()

  return (
    <div className="rfq-summary-shipping-method">
      <div className="form-field">
        <select
          id={shipMethodSelectId}
          aria-label="Shipping method"
          value={shipMethod}
          onChange={(event) => onShipMethodChange(event.target.value as RfqShipMethod | 'none')}
          className="form-field-input"
        >
          {SHIP_METHOD_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {shipMethod === 'own_account' && (
        <div
          className="form-field"
          aria-invalid={shipAccountError ? true : undefined}
          aria-describedby={shipAccountError ? shipAccountErrorId : undefined}
        >
          <ShippingAccountPicker
            shippingAccounts={shippingAccounts}
            selectedShippingAccountId={selectedShippingAccountId}
            onSelectShippingAccount={onSelectShippingAccount}
            onAddShippingAccount={onAddShippingAccount}
            name="rfq-shipping-account"
            emptyLabel="Add a shipping account to bill this request to your carrier."
          />
          {shipAccountError && (
            <p
              id={shipAccountErrorId}
              className="form-field-error"
              ref={shipAccountErrorRef}
              tabIndex={-1}
            >
              {shipAccountError}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export interface ShippingCardProps {
  readonly shipMethod: RfqShipMethod | 'none'
  readonly onShipMethodChange: (value: RfqShipMethod | 'none') => void
  readonly shippingAccounts: readonly ShippingAccountView[]
  readonly selectedShippingAccountId: string | null
  readonly onSelectShippingAccount: (id: string) => void
  readonly onAddShippingAccount: () => void
  readonly shipAccountError?: string | undefined
  readonly shipAccountErrorRef?: Ref<HTMLParagraphElement> | undefined
  readonly addresses: readonly AddressView[]
  readonly shipToAddressId: string
  readonly onShipToAddressIdChange: (value: string) => void
  readonly onAddNewAddress: () => void
}

/**
 * RFQ compose shipping card (UID-201, ADR-0076 — refines UID-117/ADR-0053 to the CHECKOUT
 * §2 control pattern applied to RFQ compose; there is no distinct RFQ-compose shipping mock).
 *
 * UID-359 item 13 (ADR-0118 D2) — `RequestSummaryRail` no longer mounts this component as one
 * block: the design interleaves "Ship to" and the shipping-method controls with the Part
 * production/Shipping/Tax rows (see {@link ShipToAddressField} and
 * {@link ShippingMethodFields}'s doc comments), which this single card cannot express. This
 * composition is kept as a thin wrapper over both pieces — for any consumer that wants the
 * whole block in one mount, and so this file's own test coverage of both fields survives in
 * one render — but `RequestSummaryRail` imports the two pieces directly instead of this
 * export. The `<h3>Shipping</h3>` heading the design never draws (`dc.html:4340-4352` has no
 * heading at all in this block) is dropped rather than replaced with an `aria-label`, since
 * there is no landmark requirement here once the block isn't its own repeated region.
 */
export function ShippingCard({
  shipMethod,
  onShipMethodChange,
  shippingAccounts,
  selectedShippingAccountId,
  onSelectShippingAccount,
  onAddShippingAccount,
  shipAccountError,
  shipAccountErrorRef,
  addresses,
  shipToAddressId,
  onShipToAddressIdChange,
  onAddNewAddress,
}: ShippingCardProps) {
  return (
    <Card className="rfq-shipping-card">
      <ShippingMethodFields
        shipMethod={shipMethod}
        onShipMethodChange={onShipMethodChange}
        shippingAccounts={shippingAccounts}
        selectedShippingAccountId={selectedShippingAccountId}
        onSelectShippingAccount={onSelectShippingAccount}
        onAddShippingAccount={onAddShippingAccount}
        shipAccountError={shipAccountError}
        shipAccountErrorRef={shipAccountErrorRef}
      />
      <ShipToAddressField
        addresses={addresses}
        shipToAddressId={shipToAddressId}
        onShipToAddressIdChange={onShipToAddressIdChange}
        onAddNewAddress={onAddNewAddress}
      />
    </Card>
  )
}
